import { AppstoreAddOutlined, ArrowLeftOutlined, CarOutlined, CheckCircleTwoTone, CommentOutlined, DollarOutlined, DownloadOutlined, DropboxOutlined, ExclamationCircleTwoTone, FileOutlined, InfoCircleOutlined, ReloadOutlined, SaveOutlined, SolutionOutlined, TagsOutlined, TeamOutlined } from "@ant-design/icons";
import { Button, Card, Col, Divider, Form, Input, message, Modal, Row, Select, Space, Spin, Table, Tabs, Tooltip, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { confirmBooking, getBookingById, unconfirmBooking, updateBooking } from "../../services/bookingApi";

import type { IBookingnew } from "../../types/bookingnew";
import Sidebar from "../Sidebar";
import HeaderPage from "../HeaderPage";

const { Title, Text, Paragraph } = Typography;

/** Abas da UI */
const TAB_CONFIG = [
  { key: "1", label: "General Details", icon: <SolutionOutlined />, subtitle: "Informações básicas do booking." },
  { key: "2", label: "Parties", icon: <TeamOutlined />, subtitle: "Partes envolvidas." },
  { key: "3", label: "References", icon: <TagsOutlined />, subtitle: "Referências e POs." },
  { key: "4", label: "Transport", icon: <CarOutlined />, subtitle: "Detalhes logísticos." },
  { key: "5", label: "Containers", icon: <AppstoreAddOutlined />, subtitle: "Quantidade e tipo." },
  { key: "6", label: "Cargo", icon: <DropboxOutlined />, subtitle: "Mercadoria e NCM." },
  { key: "7", label: "Payments", icon: <DollarOutlined />, subtitle: "Condições de pagamento." },
  { key: "8", label: "Comments", icon: <CommentOutlined />, subtitle: "Observações e notificações." },
];

/** Campos por aba (para focar a primeira com erro) */
const FIELDS_BY_TAB: Record<string, string[]> = {
  "1": ["carrier", "contractNumber", "bookingOffice"],
  "2": ["shipper", "forwarder", "consignee"],
  "3": ["shipperRefNumber", "forwardRefNumber", "purchaseOrderNumber"],
  "4": ["moveType", "placeCarrierReceipt", "earlyDepartureDate", "placeCarrierDelivery", "latestDeliveryDate"],
  "5": ["qtdContainers", "tipo_container"],
  "6": ["mercadoria"],
  "7": ["paymentChargeType", "paymentTerm", "payer", "paymentLocation"],
  "8": ["customerComment", "emailnotifications"],
};

/** Modelo para taxas na UI */
type UiTax = {
  description_taxe: string;
  category_taxe: string; // "Taxas locais da origem" | "Frete e taxas que acompanham frete"
  value: number;
  currency: string;
  applicability?: "C" | "B" | string; // C: por container, B: por BL
};

type UiBooking = {
  _id: string;
  carrier?: string;
  contractNumber?: string;
  bookingOffice?: string;

  shipper?: string;
  forwarder?: string;
  consignee?: string;

  shipperRefNumber?: string;
  forwardRefNumber?: string;
  purchaseOrderNumber?: string;

  moveType?: string;
  placeCarrierReceipt?: string;
  earlyDepartureDate?: string;
  placeCarrierDelivery?: string;
  latestDeliveryDate?: string;

  tipo_container?: string;
  qtdContainers: number;

  mercadoria?: string;

  paymentChargeType?: string;
  paymentTerm?: string;
  payer?: string;
  paymentLocation?: string;

  customerComment?: string;
  emailnotifications?: string;

  porto_embarque?: string;
  porto_descarga?: string;
  data_embarque?: string;
  data_chegada?: string;

  status?: string;

  bookingFile?: string;
  blFile?: string;

  taxas?: UiTax[];
};

/** Utilitário: ISO/Date -> dd/mm/yyyy (mantém string se já vier assim) */
function toDDMMYYYY(dateString?: string | null): string {
  if (!dateString) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) return dateString;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return String(dateString);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/** Normaliza booking do backend para o modelo de UI */
function mapToUiBooking(raw: any): UiBooking {
  const qtd = Number(raw?.qtdContainers ?? raw?.quantidade_containers ?? 1) || 1;

  const taxas: UiTax[] = Array.isArray(raw?.taxas)
    ? raw.taxas.map((t: any): UiTax => ({
        description_taxe: t.description_taxe ?? t.description ?? t.category ?? "",
        category_taxe: t.category_taxe ?? t.category ?? "",
        value: Number(t.value ?? 0),
        currency: String(t.currency ?? "").toUpperCase(),
        applicability: (t.applicability as any) ?? "C",
      }))
    : [];

  return {
    _id: String(raw?._id ?? ""),
    carrier: raw?.carrier ?? raw?.armador,
    contractNumber: raw?.contractNumber,
    bookingOffice: raw?.bookingOffice,

    shipper: raw?.shipper,
    forwarder: raw?.forwarder ?? raw?.cliente ?? raw?.email,
    consignee: raw?.consignee,

    shipperRefNumber: raw?.shipperRefNumber,
    forwardRefNumber: raw?.forwardRefNumber ?? raw?.forward_ref_number,
    purchaseOrderNumber: raw?.purchaseOrderNumber,

    moveType: raw?.moveType ?? "PortToPort",
    placeCarrierReceipt: raw?.placeCarrierReceipt ?? raw?.porto_embarque,
    earlyDepartureDate: toDDMMYYYY(raw?.earlyDepartureDate ?? raw?.data_embarque),
    placeCarrierDelivery: raw?.placeCarrierDelivery ?? raw?.porto_descarga,
    latestDeliveryDate: toDDMMYYYY(raw?.latestDeliveryDate ?? raw?.data_chegada),

    tipo_container: raw?.tipo_container,
    qtdContainers: qtd,

    mercadoria: raw?.mercadoria ?? raw?.selectMercadoria,

    paymentChargeType: raw?.paymentChargeType,
    paymentTerm: raw?.paymentTerm,
    payer: raw?.payer,
    paymentLocation: raw?.paymentLocation,

    customerComment: raw?.customerComment,
    emailnotifications: raw?.emailnotifications,

    porto_embarque: raw?.porto_embarque,
    porto_descarga: raw?.porto_descarga,
    data_embarque: raw?.data_embarque,
    data_chegada: raw?.data_chegada,

    status: raw?.status,

    bookingFile: raw?.bookingFile,
    blFile: raw?.blFile,

    taxas,
  };
}

/** Payload de update */
function buildUpdatePayload(values: any, current: UiBooking): Partial<IBookingnew> & Record<string, any> {
  return {
    _id: current._id,
    armador: current.carrier ?? values.carrier,

    carrier: values.carrier,
    contractNumber: values.contractNumber,
    bookingOffice: values.bookingOffice,

    shipper: values.shipper,
    forwarder: values.forwarder,
    consignee: values.consignee,

    shipperRefNumber: values.shipperRefNumber,
    forwardRefNumber: values.forwardRefNumber,
    purchaseOrderNumber: values.purchaseOrderNumber,

    moveType: values.moveType,
    placeCarrierReceipt: values.placeCarrierReceipt,
    earlyDepartureDate: values.earlyDepartureDate,
    placeCarrierDelivery: values.placeCarrierDelivery,
    latestDeliveryDate: values.latestDeliveryDate,

    tipo_container: values.tipo_container,
    qtdContainers: values.qtdContainers,
    quantidade_containers: values.qtdContainers,

    mercadoria: values.mercadoria,
    selectMercadoria: values.mercadoria,

    paymentChargeType: values.paymentChargeType,
    paymentTerm: values.paymentTerm,
    payer: values.payer,
    paymentLocation: values.paymentLocation,

    customerComment: values.customerComment,
    emailnotifications: values.emailnotifications,

    // reenviando taxas atuais como estão
    taxas: current.taxas?.map((t) => ({
      description_taxe: t.description_taxe,
      category_taxe: t.category_taxe,
      value: t.value,
      currency: t.currency,
      applicability: t.applicability ?? "C",
    })),
  };
}

const ShowBooking: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [activeKey, setActiveKey] = useState("1");
  const [tabError, setTabError] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uiBooking, setUiBooking] = useState<UiBooking | null>(null);
  const [qtdContainers, setQtdContainers] = useState<number>(1);
  const [modal, setModal] = useState<{ visible: boolean; title: string; message: string }>({
    visible: false,
    title: "",
    message: "",
  });

  /** Carrega booking */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data: IBookingnew = await getBookingById(id!);
        if (!mounted) return;
        const mapped = mapToUiBooking(data);
        setUiBooking(mapped);
        setQtdContainers(mapped.qtdContainers || 1);

        // Preenche o form
        form.setFieldsValue({
          carrier: mapped.carrier,
          contractNumber: mapped.contractNumber,
          bookingOffice: mapped.bookingOffice,
          shipper: mapped.shipper,
          forwarder: mapped.forwarder,
          consignee: mapped.consignee,
          shipperRefNumber: mapped.shipperRefNumber,
          forwardRefNumber: mapped.forwardRefNumber,
          purchaseOrderNumber: mapped.purchaseOrderNumber,
          moveType: mapped.moveType,
          placeCarrierReceipt: mapped.placeCarrierReceipt,
          earlyDepartureDate: mapped.earlyDepartureDate,
          placeCarrierDelivery: mapped.placeCarrierDelivery,
          latestDeliveryDate: mapped.latestDeliveryDate,
          qtdContainers: mapped.qtdContainers,
          tipo_container: mapped.tipo_container,
          mercadoria: mapped.mercadoria,
          paymentChargeType: mapped.paymentChargeType,
          paymentTerm: mapped.paymentTerm,
          payer: mapped.payer,
          paymentLocation: mapped.paymentLocation,
          customerComment: mapped.customerComment,
          emailnotifications: mapped.emailnotifications,
        });
      } catch (e: any) {
        message.error(e?.response?.data?.error || "Falha ao carregar booking");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, form]);

  /** Marca abas com erro e foca a primeira */
  const setTabsWithErrors = (fieldsError: { name: (string | number)[] }[] = []) => {
    const next: Record<string, boolean> = {};
    for (const t of TAB_CONFIG) {
      if (fieldsError.some((err) => (FIELDS_BY_TAB[t.key] || []).includes(String(err.name?.[0])))) {
        next[t.key] = true;
      }
    }
    setTabError(next);
    const firstWithError = TAB_CONFIG.find((t) => next[t.key]);
    if (firstWithError) setActiveKey(firstWithError.key);
  };

  /** Valida e executa */
  const validateAnd = async (fn: (vals: any) => Promise<void> | void) => {
    try {
      const values = await form.validateFields();
      setTabError({});
      await fn(values);
    } catch (err: any) {
      if (err?.errorFields) setTabsWithErrors(err.errorFields);
      message.error("Preencha os campos obrigatórios.");
    }
  };

  /** Helpers de taxas */
  const calcLine = (t: UiTax, containers: number) => {
    const base = Number(t.value || 0);
    if ((t.applicability ?? "C") === "B") return base;
    return base * (containers || 1);
  };

  const freightTaxes = useMemo(
    () => (uiBooking?.taxas || []).filter((t) => t.category_taxe === "Frete e taxas que acompanham frete"),
    [uiBooking]
  );
  const originTaxes = useMemo(
    () => (uiBooking?.taxas || []).filter((t) => t.category_taxe === "Taxas locais da origem"),
    [uiBooking]
  );

  const oceanTotal = useMemo(
    () => freightTaxes.reduce((acc, t) => acc + calcLine(t, qtdContainers), 0),
    [freightTaxes, qtdContainers]
  );

  /** Salvar alterações */
  const onSave = async (values: any) => {
    if (!uiBooking) return;
    try {
      setSaving(true);
      const payload = buildUpdatePayload(values, uiBooking);
      const updated = await updateBooking(uiBooking._id, payload);
      const mapped = mapToUiBooking(updated);
      setUiBooking(mapped);
      setQtdContainers(mapped.qtdContainers || 1);
      setModal({ visible: true, title: "Sucesso", message: "Alterações salvas com sucesso." });
      message.success("Booking atualizado.");
    } catch (e: any) {
      setModal({
        visible: true,
        title: "Erro",
        message: e?.response?.data?.error || "Falha ao salvar alterações",
      });
      message.error("Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  /** Confirmar */
  const onConfirm = async () => {
    if (!uiBooking) return;
    try {
      setSaving(true);
      const confirmed = await confirmBooking(uiBooking._id);
      const mapped = mapToUiBooking(confirmed);
      setUiBooking(mapped);
      setModal({
        visible: true,
        title: "Booking Confirmado",
        message: "O booking foi solicitado ao armador, gentileza aguardar o Booking Number que sera enviado no seu e-mail.",
      });
      message.success("Booking confirmado!");
    } catch (e: any) {
      message.error(e?.response?.data?.error || "Falha ao confirmar booking");
    } finally {
      setSaving(false);
    }
  };

  /** Desconfirmar */
  const onUnconfirm = async () => {
    if (!uiBooking) return;
    try {
      setSaving(true);
      const unconfirmed = await unconfirmBooking(uiBooking._id);
      const mapped = mapToUiBooking(unconfirmed);
      setUiBooking(mapped);
      setModal({
        visible: true,
        title: "Confirmação removida",
        message: "O status de confirmação foi revertido com sucesso.",
      });
      message.success("Confirmação removida.");
    } catch (e: any) {
      message.error(e?.response?.data?.error || "Falha ao desconfirmar booking");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 280 }}>
        <Spin tip="Carregando booking..." />
      </div>
    );
  }

  if (!uiBooking) {
    return (
      <Card style={{ borderRadius: 16 }}>
        <Title level={4}>Booking não encontrado</Title>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Voltar
        </Button>
      </Card>
    );
  }

  return (
    <div className="flex-dashboard">
      <Sidebar elementoAtivo="bookings"/>
      <main>
        <HeaderPage nomeOpcao="Booking"/>
        <div className="main-content">
    <div style={{ background: "#f4f8fb", padding: "32px 0", minHeight: "100vh" }}>
      <Row gutter={32} justify="center">
        <Col xs={24} md={15} xl={15}>
          <Card
            style={{ borderRadius: 16, boxShadow: "0 3px 24px #0066cc18", padding: 0, background: "#fff" }}
            bodyStyle={{ padding: 0 }}
          >
            <div style={{ padding: "32px 32px 0 32px" }}>
              <Title level={2} style={{ color: "#0066cc", marginBottom: 0 }}>
                <SolutionOutlined style={{ marginRight: 10 }} />
                Booking #{String(uiBooking._id).slice(0, 6)}
              </Title>
              <Text type="secondary" style={{ fontSize: 16 }}>
                Visualize e edite as informações do seu booking.
              </Text>
              <Divider style={{ marginTop: 12, marginBottom: 24 }} />
            </div>

            <div style={{ padding: "0 32px 24px 32px" }}>
              <Form form={form} layout="vertical" size="middle">
                <Tabs activeKey={activeKey} onChange={setActiveKey} type="card" tabBarGutter={8} moreIcon={null}>
                  {TAB_CONFIG.map((tab) => (
                    <Tabs.TabPane
                      key={tab.key}
                      tab={
                        <span>
                          {tab.icon}
                          <span style={tabError[tab.key] ? { color: "red" } : undefined}>
                            {tab.label}
                            {tabError[tab.key] && (
                              <ExclamationCircleTwoTone twoToneColor="#f5222d" style={{ marginLeft: 6 }} />
                            )}
                          </span>
                        </span>
                      }
                    >
                      <Title level={4} style={{ marginBottom: 0, marginTop: 10 }}>
                        {tab.icon} {tab.label}
                      </Title>
                      <Text type="secondary" style={{ marginBottom: 12, display: "block" }}>
                        {tab.subtitle}
                      </Text>
                      <Divider style={{ margin: "8px 0 24px" }} />

                      {tab.key === "1" && (
                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item
                              label={
                                <span>
                                  Carrier / NVOCC&nbsp;
                                  <Tooltip title="Transportadora ou agente do booking">
                                    <InfoCircleOutlined />
                                  </Tooltip>
                                </span>
                              }
                              name="carrier"
                              rules={[{ required: true, message: "Obrigatório" }]}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              label="Contract Number*"
                              name="contractNumber"
                              rules={[{ required: true, message: "Obrigatório" }]}
                            >
                              <Input placeholder="Digite o número do contrato" />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item label="Booking Office" name="bookingOffice">
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}

                      {tab.key === "2" && (
                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item label="Shipper" name="shipper">
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item label="Forwarder" name="forwarder">
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item label="Consignee" name="consignee">
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}

                      {tab.key === "3" && (
                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item label="Shipper Reference Number" name="shipperRefNumber">
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item label="Forwarder's Reference Number" name="forwardRefNumber">
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item label="Purchase Order Number" name="purchaseOrderNumber">
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}

                      {tab.key === "4" && (
                        <>
                          <Row gutter={16}>
                            <Col span={8}>
                              <Form.Item
                                label="Move Type"
                                name="moveType"
                                rules={[{ required: true, message: "Obrigatório" }]}
                              >
                                <Select>
                                  <Select.Option value="PortToPort">Port, Ramp, or CY to Port, Ramp, or CY</Select.Option>
                                  <Select.Option value="DoorToPort">Door to port, Ramp, or Cy</Select.Option>
                                  <Select.Option value="DoorToDoor">Door to Door</Select.Option>
                                  <Select.Option value="PortToDoor">Port Ramp, or Cy to Door</Select.Option>
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                label="Place of Carrier Receipt"
                                name="placeCarrierReceipt"
                                rules={[{ required: true, message: "Obrigatório" }]}
                              >
                                <Input />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                label="Early Departure Date"
                                name="earlyDepartureDate"
                                rules={[{ required: true, message: "Obrigatório" }]}
                              >
                                <Input />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={8}></Col>
                            <Col span={8}>
                              <Form.Item
                                label="Place of Carrier Delivery"
                                name="placeCarrierDelivery"
                                rules={[{ required: true, message: "Obrigatório" }]}
                              >
                                <Input />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                label="Latest Delivery Date"
                                name="latestDeliveryDate"
                                rules={[{ required: true, message: "Obrigatório" }]}
                              >
                                <Input />
                              </Form.Item>
                            </Col>
                          </Row>
                        </>
                      )}

                      {tab.key === "5" && (
                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item
                              label="Quantidade de Containers"
                              name="qtdContainers"
                              rules={[{ required: true, message: "Obrigatório" }]}
                            >
                              <Input
                                type="number"
                                min={1}
                                value={qtdContainers}
                                onChange={(e) => setQtdContainers(Number(e.target.value))}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={16}>
                            <Form.Item
                              label="Tipo de Container"
                              name="tipo_container"
                              rules={[{ required: true, message: "Obrigatório" }]}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}

                      {tab.key === "6" && (
                        <Form.Item label="Mercadoria" name="mercadoria" rules={[{ required: true, message: "Obrigatório" }]}>
                          <Input placeholder="Digite a mercadoria/NCM" />
                        </Form.Item>
                      )}

                      {tab.key === "7" && (
                        <Row gutter={16}>
                          <Col span={6}>
                            <Form.Item
                              label="Charge Type*"
                              name="paymentChargeType"
                              rules={[{ required: true, message: "Obrigatório" }]}
                            >
                              <Select>
                                <Select.Option value="Additional">Additional Charges</Select.Option>
                                <Select.Option value="OceanFreight">Basic Freight</Select.Option>
                                <Select.Option value="DestinationHaulage">Destination Haulage Charges</Select.Option>
                                <Select.Option value="DetinationTerminalHandling">Destination Port Charges</Select.Option>
                                <Select.Option value="OriginTerminalHandling">Origin Port Charges</Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item label="Payment Term* " name="paymentTerm" rules={[{ required: true, message: "Obrigatório" }]}>
                              <Select>
                                <Select.Option value="Prepaid">Pre-paid</Select.Option>
                                <Select.Option value="Collect">Collect</Select.Option>
                                <Select.Option value="PayableElsehere">Payable Elsewhere</Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item label="Payer* " name="payer" rules={[{ required: true, message: "Obrigatório" }]}>
                              <Select>
                                <Select.Option value="Booker">Booker</Select.Option>
                                <Select.Option value="Consignee">Consignee</Select.Option>
                                <Select.Option value="Contract Party">Contract Party</Select.Option>
                                <Select.Option value="Forwarder">Forwarder</Select.Option>
                                <Select.Option value="Main Notify Party">Main Notify Party</Select.Option>
                                <Select.Option value="First Additional">First Additional Notify Party</Select.Option>
                                <Select.Option value="Second Additional">Second Additional Notify Party</Select.Option>
                                <Select.Option value="Shipper">Shipper</Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item label="Payment Location" name="paymentLocation">
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}

                      {tab.key === "8" && (
                        <>
                          <Form.Item label="Customer Comments" name="customerComment">
                            <Input.TextArea rows={3} />
                          </Form.Item>
                          <Form.Item label="Partner Email Notifications" name="emailnotifications">
                            <Input type="email" />
                          </Form.Item>
                        </>
                      )}
                    </Tabs.TabPane>
                  ))}
                </Tabs>

                <Divider />
                <Row justify="space-between" style={{ marginTop: 8 }}>
                  <Col>
                    <Space>
                      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ borderRadius: 20 }}>
                        Voltar
                      </Button>
                      <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()} style={{ borderRadius: 20 }}>
                        Recarregar
                      </Button>
                    </Space>
                  </Col>
                  <Col>
                    <Space>
                      {uiBooking.bookingFile ? (
                        <Tooltip title="Download Booking">
                          <Button icon={<DownloadOutlined />} onClick={() => window.open(uiBooking.bookingFile!, "_blank")} />
                        </Tooltip>
                      ) : null}
                      {uiBooking.blFile ? (
                        <Tooltip title="Download BL">
                          <Button icon={<FileOutlined />} onClick={() => window.open(uiBooking.blFile!, "_blank")} />
                        </Tooltip>
                      ) : null}
                      <Button
                        type="default"
                        icon={<SaveOutlined />}
                        size="large"
                        loading={saving}
                        style={{ background: "#ecf6fe", borderColor: "#0066cc", color: "#0066cc", fontWeight: 500, borderRadius: 24 }}
                        onClick={() => validateAnd(onSave)}
                      >
                        Salvar alterações
                      </Button>

                      {uiBooking.status !== "Confirmed" ? (
                        <Button
                          type="primary"
                          icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
                          size="large"
                          loading={saving}
                          style={{ fontWeight: 500, borderRadius: 24 }}
                          onClick={() => validateAnd(() => onConfirm())}
                        >
                          Confirmar
                        </Button>
                      ) : (
                        <Button
                          danger
                          size="large"
                          loading={saving}
                          style={{ fontWeight: 500, borderRadius: 24 }}
                          onClick={() => validateAnd(() => onUnconfirm())}
                        >
                          Desconfirmar Confirmação
                        </Button>
                      )}
                    </Space>
                  </Col>
                </Row>
              </Form>
            </div>
          </Card>
        </Col>

        {/* Painel de custos */}
        <Col xs={24} md={9} xl={8}>
          <Card
            title={
              <span>
                <DollarOutlined style={{ color: "#009688", marginRight: 6 }} />
                <b>Resumo de Custos</b>
              </span>
            }
            bordered={false}
            style={{ borderRadius: 16, background: "#fcfcfc", boxShadow: "0 2px 12px #0066cc18", marginTop: 8 }}
            headStyle={{ background: "#f7fafd", borderRadius: "16px 16px 0 0" }}
          >
            <Text strong type="secondary">Ocean Freight</Text>
            <Table
              size="small"
              pagination={false}
              style={{ marginTop: 10, marginBottom: 16 }}
              dataSource={(freightTaxes || []).map((tax, i) => ({
                key: `${tax.description_taxe}-${i}`,
                category: tax.description_taxe,
                valor: (calcLine(tax, qtdContainers)).toFixed(2) + " " + (tax.currency || ""),
              }))}
              columns={[
                { title: "Descrição", dataIndex: "category" },
                { title: "Valor", dataIndex: "valor", align: "right" as const },
              ]}
            />

            <Text strong type="secondary">Taxas Locais</Text>
            <Table
              size="small"
              pagination={false}
              style={{ marginTop: 10, marginBottom: 16 }}
              dataSource={(originTaxes || []).map((tax, i) => ({
                key: `${tax.description_taxe}-${i}`,
                category: tax.description_taxe,
                valor: (calcLine(tax, qtdContainers)) + " " + (tax.currency || ""),
              }))}
              columns={[
                { title: "Descrição", dataIndex: "category" },
                { title: "Valor", dataIndex: "valor", align: "right" as const },
              ]}
            />

            <Divider style={{ margin: "16px 0" }} />
            <Title level={5} style={{ marginBottom: 4 }}>
              <DollarOutlined style={{ color: "#0066cc", marginRight: 6 }} />
              Total Frete a Pagar
            </Title>
            <Text strong style={{ fontSize: 22 }}>
              {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(oceanTotal)}
              <span style={{ color: "#999", fontSize: 16 }}> + taxas</span>
            </Text>
            <Divider style={{ margin: "16px 0" }} />
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              <InfoCircleOutlined style={{ color: "#009688", marginRight: 6 }} />
              Revise todos os valores antes de salvar.
            </Paragraph>
          </Card>
        </Col>
      </Row>

      {/* Modal feedback */}
      <Modal
        open={modal.visible}
        title={modal.title}
        onCancel={() => setModal({ ...modal, visible: false })}
        footer={[
          <Button key="close" type="primary" onClick={() => setModal({ ...modal, visible: false })} style={{ borderRadius: 20 }}>
            Fechar
          </Button>,
        ]}
        centered
      >
        <Text>{modal.message}</Text>
      </Modal>
    </div>
    </div>
    </main>
    </div>
    
  );
};

export default ShowBooking;
